import { Inject, Injectable } from '@angular/core';

import { BetslipConfig, MyBetsVisualizationTrackingConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { generateGuid } from '@frontend/sports/common/core/utils/string';
import { OddsFormatterService } from '@frontend/sports/odds/feature';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { NOT_APPLICABLE, TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { WidgetType } from '@frontend/sports/types/components/widget';
import { UserService } from '@frontend/sports/user/feature';
import { NativeAppService } from '@frontend/vanilla/core';
import { findIndex, uniq } from 'lodash-es';

import { BestOddsGuaranteedService } from '../../../../lib/best-odds-guarantee/services/best-odds-guaranteed.service';
import { VisualizationTracking, VisualizationType } from '../../../../lib/my-bets/models/my-bets-visualization-types';
import { NumpadAction } from '../../../../lib/numpad/model';
import { NumpadTrackingService } from '../../../../lib/numpad/numpad-tracking.service';
import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { IBetslipState } from '../../../base/store/state';
import { isLinear } from '../../../base/utils/linear-utils';
import { BetslipType } from '../../../core/betslip-type';
import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { isBetBuilderPick } from '../../../core/utils';
import { FinishPlaceBetPayload } from '../../betplacement/actions';
import { isSucceededNonPartialSlip } from '../../betplacement/utils';
import { QB_TRACKING_SUFFIX } from '../../quick-bet/const';
import { IRewardTokensState, TokensRecord } from '../../reward-tokens/state';
import { IBetslipTypeState } from '../../types/state';
import { BetslipLinearTracking, BetslipTrackingLocation, PickBetTrackingTypes, QuickBetTrackingLocation } from '../models';
import { BetslipNavigationTracking, TrackAddRemoveBet, TrackBetslipPick, TrackPlaceBet, TrackingSlipData } from '../tracking.models';
import { BetslipBaseTrackingService } from './betslip-base-tracking.service';
import { BetslipTrackingHelper } from './betslip-tracking-helper';

@Injectable()
export class BetslipDigitalTrackingService extends BetslipBaseTrackingService {
    constructor(
        numpadTracking: NumpadTrackingService,
        private userService: UserService,
        nativeService: NativeAppService,
        oddsFormatterService: OddsFormatterService,
        mybetsVisualizationTrackingConfig: MyBetsVisualizationTrackingConfig,
        protected override trackingService: TrackingService,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) protected override messageProvider: IBetslipMessagesProviderService,
        private betslipConfig: BetslipConfig,
        private bogService: BestOddsGuaranteedService,
    ) {
        super(numpadTracking, trackingService, nativeService, oddsFormatterService, mybetsVisualizationTrackingConfig, messageProvider);
    }

    private isQuickbet(pickList: BetslipPick[], pickInput: BetslipPick): boolean {
        const quickbetPick = pickList.find(
            (pick) =>
                pick.id.isEqual(pickInput.id) && pick.tracking.sourceOfBetSuffix && pick.tracking.sourceOfBetSuffix.indexOf(QB_TRACKING_SUFFIX) > -1,
        );

        return !!quickbetPick || !!(pickInput.tracking.sourceOfBetSuffix && pickInput.tracking.sourceOfBetSuffix.indexOf(QB_TRACKING_SUFFIX) > -1);
    }

    private getPickVisualization(pick: BetslipPick): VisualizationTracking {
        const { isEnabled, allowedLeagues, marketTemplateMapping, marketTypeMapping } = this.mybetsVisualizationTrackingConfig;
        const leagueId = pick.tracking.parentLeagueId;
        const isTrackingEnabled = isEnabled && !!leagueId && allowedLeagues[pick.sportId]?.includes(leagueId);

        if (!isTrackingEnabled) {
            return this.noVisualization();
        }

        const { mappings, marketIdentifier } = BetslipV1Pick.isPick(pick)
            ? {
                  mappings: marketTemplateMapping?.[pick.sportId],
                  marketIdentifier: pick.market.templateId!,
              }
            : {
                  mappings: marketTypeMapping?.[pick.sportId],
                  marketIdentifier: pick.market.marketType!.toString(),
              };

        if (!mappings) {
            return this.noVisualization();
        }

        const visualizationType = Object.keys(mappings).find((mapType) =>
            (mappings[mapType] as (number | string | undefined)[]).includes(marketIdentifier),
        );

        return {
            isTrackingEnabled,
            visualizationType: (visualizationType as VisualizationType) || VisualizationType.NoVisualization,
        };
    }

    private noVisualization(): VisualizationTracking {
        return {
            isTrackingEnabled: false,
            visualizationType: VisualizationType.NoVisualization,
        };
    }

    trackAddRemovePick(eventName: string, pickInput: BetslipPick, state: IBetslipState, marketId?: string, groupId?: string) {
        if (!pickInput.tracking) {
            return;
        }
        const isQB = this.isQuickbet(state.picks.pickList, pickInput);
        pickInput.tracking.additional =
            pickInput.tracking.sourceOfPick === SourceOfPick.Upsell || pickInput.tracking.additional === undefined
                ? {}
                : pickInput.tracking.additional;
        Object.assign(pickInput.tracking.additional, BetslipTrackingHelper.getAdditional(pickInput.tracking));
        pickInput.tracking.sourceOfBetSuffix = isQB ? QB_TRACKING_SUFFIX : '';
        const pickTracking = {
            ...this.getPickTracking(pickInput, state.types),
            ...pickInput.tracking.additional,
            ...this.getOptionalPickTracking(pickInput, marketId),
            [trackingConstants.COMPONENT_CONTENT_POSITION]: pickInput.tracking.contentPosition || NOT_APPLICABLE,
            [trackingConstants.BET_TYPE]:
                pickInput.tracking.additional?.[trackingConstants.COMPONENT_MODULE_NAME] === WidgetType.TopEvents && pickInput.tracking.betType
                    ? pickInput.tracking.betType
                    : NOT_APPLICABLE,
            [trackingConstants.COMPONENT_BET_SOURCE]: `${pickInput.tracking.source || NOT_APPLICABLE}${pickInput.tracking.sheetviewSuffix ?? ''}`,
            [trackingConstants.BET_DETAILS]: groupId
                ? BetslipTrackingHelper.getLegBetDetails(pickInput, groupId)
                : pickInput.boostedPriceDetails?.isPriceBoosted
                  ? BetslipTrackingHelper.getBetDetails(pickInput.tracking, this.nativeService.isTerminal)
                  : pickInput.tracking.source === 'NextRaces'
                    ? pickInput.tracking.additional?.[trackingConstants.BET_DETAILS]
                    : (pickInput.tracking.betDetails ?? NOT_APPLICABLE),
            [trackingConstants.QUICKBET]: isQB ? 'yes' : NOT_APPLICABLE,
            [trackingConstants.COMPONENT_MODULE_TYPE]: pickInput.tracking.moduleType || NOT_APPLICABLE,
        };
        if (eventName === trackingConstants.EVENT_CART_ADD) {
            (pickTracking[trackingConstants.SELECTIONS_IN_SLIP as keyof typeof pickTracking] as number) = state.picks.pickList.length;
        }

        if (pickInput.tracking.gameDetails) {
            pickTracking[trackingConstants.GAME_DETAILS] = pickInput.tracking.gameDetails;
        }

        if (pickInput.tracking.variant) {
            pickTracking[trackingConstants.VARIANT] = pickInput.tracking.variant;
        }

        if (pickInput.tracking.betPickType) {
            pickTracking[trackingConstants.BET_PICK_TYPE] = pickInput.tracking.betPickType;
        }

        this.trackingService.track(eventName, { transactionProducts: pickTracking } as TrackAddRemoveBet);
    }

    trackBetPlacementStart(state: IBetslipState, requestId: string, slips: TrackingSlipData[]): void {
        if (!slips.length) {
            return;
        }

        const picks = slips.flatMap((slip) => slip.picks);
        const totalStake = slips.reduce((acc, curr) => acc + (curr.stake ?? 0), 0);

        const types = uniq(slips.map((s) => s.type).filter((t): t is BetslipType => !!t));

        const transactionProducts = slips.flatMap((slip) => this.getDigitalTransactionProducts(slip, state.types, requestId, state.rewardTokens));

        const betslipTrackingData = this.getBetslipTrackingData(
            picks,
            requestId,
            state.types,
            types,
            transactionProducts,
            state.rewardTokens.tokens,
            trackingConstants.PlaceBet.PAGE_NAME,
            isLinear(state),
            totalStake,
        );

        betslipTrackingData[trackingConstants.CART_PURCHASE] = trackingConstants.EVENT_CHECKOUT;

        const betDetails = types
            .map((type) => (type === BetslipType.Teaser ? `${BetslipType.Teaser} - ${state.types.teaserBet.selectedPoints} points` : type))
            .join(',');

        (betslipTrackingData[trackingConstants.BET_DETAILS as keyof typeof betslipTrackingData] as string) = betDetails;

        this.trackingService.track(trackingConstants.EVENT_CART_CHECKOUT, betslipTrackingData);
    }

    // Discuss whether we need to track this once for each slip

    trackBetPlacementResult(data: FinishPlaceBetPayload): void {
        const successfulSlips = data.placeBetResult.slips.filter(isSucceededNonPartialSlip);

        if (!successfulSlips.length) {
            return;
        }

        let allPicks: BetslipPick[] = [];
        let transactionProducts: TrackBetslipPick[] = [];

        let totalStake = 0;

        successfulSlips.forEach((slip) => {
            const trackingSlip: TrackingSlipData = {
                stake: slip.stake ?? 0,
                picks: slip.picks.map((p) => p.clientPick).filter(isDefined),
                type: BetslipTrackingHelper.getSuccessSlipTrackingBetslipType(slip),
            };

            const picks = slip.picks.filter((pick) => !!pick.clientPick).map((pick) => pick.clientPick!);

            totalStake += slip.stake ?? 0;

            allPicks = [...allPicks, ...picks];

            transactionProducts = [
                ...transactionProducts,
                ...this.getDigitalTransactionProducts(
                    trackingSlip,
                    data.state.types,
                    data.request.requestId,
                    data.state.rewardTokens,
                    slip.betNumber,
                ),
            ];
        });

        const betslipTrackingData = this.getBetslipTrackingData(
            allPicks,
            data.request.requestId,
            data.state.types,
            successfulSlips.map((x) => BetslipTrackingHelper.mapBetslipType(x.betType)),
            transactionProducts,
            data.state.rewardTokens.tokens,
            trackingConstants.PlaceBetSuccess.PAGE_NAME,
            isLinear(data.state),
            totalStake,
        );

        betslipTrackingData[trackingConstants.COMPONENT_EVENT_POSITION] = !data.bettingStatus?.hasBets
            ? trackingConstants.FIRST_BET
            : trackingConstants.OTHER_BET;

        betslipTrackingData[trackingConstants.COMPONENT_SLIP_ID] = successfulSlips.map((s) => s.betNumber).join(',');
        betslipTrackingData[trackingConstants.CART_PURCHASE] = trackingConstants.EVENT_PURCHASE;
        (betslipTrackingData[trackingConstants.COMPONENT_EVENT_DETAILS as keyof typeof betslipTrackingData] as string) =
            this.oddsAcceptanceMessages.find((item) => item.key === data.state.settings.oddsAcceptance)?.value!;

        this.trackingService.track(trackingConstants.EVENT_CART_PURCHASE, betslipTrackingData);
    }

    trackBetTypeChange(betslipType: BetslipType | null) {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: BetslipTrackingLocation,
            [trackingConstants.COMPONENT_LABEL_EVENT]: BetslipNavigationTracking.TrackingLabelEvent,
            [trackingConstants.COMPONENT_ACTION_EVENT]: BetslipNavigationTracking.Click,
            [trackingConstants.COMPONENT_POSITION_EVENT]: BetslipNavigationTracking.TrackingPositionEvent,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: betslipType
                ? findIndex(Object.values(BetslipType), (type: BetslipType) => betslipType === type)
                : 0,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: BetslipTrackingLocation,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: betslipType ? BetslipTrackingHelper.toUpperFirst(betslipType) : '',
        });
    }

    trackClosePopUp() {
        this.trackingService.track(trackingConstants.EVENT_PAGE_VIEW, { [trackingConstants.PAGE_NAME]: 'M2_slip_action_ClosePopup' });
    }

    private getDigitalTransactionProducts(
        slip: TrackingSlipData,
        typeStake: IBetslipTypeState,
        requestId: string,
        rewardTokens: IRewardTokensState,
        betslipId?: string,
    ): TrackBetslipPick[] {
        const slipBetType = slip.type;

        const picks = Object.values(slip.picks);
        const stake = slip.stake ?? 0;

        return picks.flatMap((betslipPick) => {
            const pickModel = BaseBetslipGroupPick.isPick(betslipPick)
                ? { odds: betslipPick.acceptedPrice.price?.nativeOdds, legs: betslipPick.getLegs(), groupId: betslipPick.groupInfo.groupId }
                : { legs: [betslipPick] };

            return pickModel.legs.map((pick) => {
                const tracking = pick.tracking;
                tracking.additional = tracking.additional ?? {};
                Object.assign(tracking.additional, BetslipTrackingHelper.getAdditional(tracking));

                const isQB = tracking ? tracking.sourceOfBetSuffix && tracking.sourceOfBetSuffix.indexOf(QB_TRACKING_SUFFIX) > -1 : false;
                const numpadData = this.numpadTracking.getStakeSelectedData(
                    isQB ? QuickBetTrackingLocation : BetslipTrackingLocation,
                    stake.toString(),
                    //magic casting :)
                    tracking.numpadAction as NumpadAction,
                );

                const visualizationTracking = this.getPickVisualization(pick);

                const betPickType = tracking.betPickType ?? this.getBetPickTypeFromSlip(slipBetType, picks);

                const trackDetails = {
                    ...this.getTransactionProduct(pick, typeStake, slipBetType, rewardTokens.tokens, picks.length, stake, betslipId, pickModel.odds),
                    ...numpadData,
                    betRequestID: requestId,
                    ...tracking.additional,
                    [trackingConstants.COMPONENT_CONTENT_POSITION]: pick.tracking.contentPosition || NOT_APPLICABLE,
                    [trackingConstants.RESULT_ID]: BetslipTrackingHelper.getResultId(pick) || NOT_APPLICABLE,
                    [trackingConstants.SPORT_ID]: pick.sportId || NOT_APPLICABLE,
                    [trackingConstants.MARKET_ID]: pick.market.id.toString() || NOT_APPLICABLE,
                    [trackingConstants.SPORT_LEAGUE_ID]: BetslipTrackingHelper.getLeagueId(pick) || NOT_APPLICABLE,
                    [trackingConstants.PRODUCT_PICK_DETAILS]: BetslipTrackingHelper.getPickDetails(pick),
                    [trackingConstants.COMPONENT_BET_SOURCE]: `${tracking.source || NOT_APPLICABLE}${tracking.sheetviewSuffix ?? ''}`,
                    [trackingConstants.BET_DETAILS]: pickModel.groupId
                        ? BetslipTrackingHelper.getLegBetDetails(pick, pickModel.groupId)
                        : this.getBetDetails(pick, typeStake, slipBetType),
                    [trackingConstants.COMPONENT_MODULE_TYPE]: pick.tracking.moduleType,
                    [trackingConstants.BET_CALENDER]: pick.tracking.betCalender || pick.tracking.additional?.betCalender || NOT_APPLICABLE,
                    [trackingConstants.BET_TRACKING_TYPE]: visualizationTracking.visualizationType,
                    [trackingConstants.BET_PICK_TYPE]: betPickType,
                } as TrackBetslipPick;

                if (pick.tracking.variant) {
                    trackDetails[trackingConstants.VARIANT] = pick.tracking.variant;
                }

                return trackDetails;
            });
        });
    }

    private getBetDetails(pick: BetslipPick, typeStake: IBetslipTypeState, betType: BetslipType | null): string {
        if (betType === BetslipType.Teaser) {
            return betType + ' - ' + typeStake.teaserBet.selectedPoints + ' points';
        }

        if (pick.boostedPriceDetails?.isPriceBoosted) {
            return BetslipTrackingHelper.getBetDetails(pick.tracking, this.nativeService.isTerminal);
        }

        if (pick.sportId === SportConstant.Horses) {
            const fixture = BetslipTrackingHelper.getHorseRacingFixture(pick);
            return this.bogService.isFixtureBogEligible(fixture) ? 'BOG' : NOT_APPLICABLE;
        }

        return pick.tracking.betDetails ?? NOT_APPLICABLE;
    }

    private getBetslipTrackingData(
        picks: BetslipPick[],
        requestId: string,
        types: IBetslipTypeState,
        betTypes: BetslipType[],
        transactionProducts: TrackBetslipPick[],
        tokens: TokensRecord,
        pageName: string,
        isLinearMode: boolean,
        totalStake: number,
    ): TrackPlaceBet {
        const stakePerPick = totalStake / picks.length;
        const uniqueBetTypes = [...new Set(betTypes)];

        picks.forEach((p) => {
            const pointer = this.betSource[p.isLive ? 'live' : 'main'];
            const sourceOfBet = BetslipTrackingHelper.getPickTrackingName(p);
            pointer[sourceOfBet] = (pointer[sourceOfBet] || 0) + 1;
        });

        return {
            transactionId: generateGuid(),
            transactionAffiliation: BetslipTrackingHelper.getTransactionAffiliation(picks),
            transactionTotal: Math.round(this.getStake(types) * 100),
            transactionProducts,
            numberOfBets: picks.length,
            currencyCode: this.userService.currency,
            [trackingConstants.PRODUCT_BET_SPORTS]: BetslipTrackingHelper.getSourceOfBet(
                this.betSource,
                stakePerPick,
                uniqueBetTypes.join('-'),
                uniqueBetTypes.map((betType) => BetslipTrackingHelper.getRewardToken(tokens, types, betType, picks)).join('-'),
            ),
            [trackingConstants.PAGE_NAME]: pageName,
            [trackingConstants.BETSLIP_TYPE]: this.getLocation(isLinearMode),
            betRequestID: requestId,
        } as TrackPlaceBet;
    }

    private getBetPickTypeFromSlip(slipBetslipType: BetslipType | null, slipPickList: BetslipPick[]): string | undefined {
        if (!slipBetslipType || !slipPickList.length) {
            return undefined;
        }

        if (slipBetslipType === BetslipType.System) {
            return PickBetTrackingTypes.System;
        }

        if (slipBetslipType === BetslipType.Teaser) {
            return PickBetTrackingTypes.Teaser;
        }

        if (slipPickList.length === 1) {
            return isBetBuilderPick(slipPickList[0]) ? PickBetTrackingTypes.Sgp : PickBetTrackingTypes.Single;
        }

        return slipPickList.some(isBetBuilderPick) ? PickBetTrackingTypes.SgpPlus : PickBetTrackingTypes.Parlay;
    }

    private getLocation(isLinearMode: boolean): string {
        if (!isLinearMode) {
            return BetslipLinearTracking.Tabbed;
        }

        return this.betslipConfig.linearModeCheckboxesEnabled ? BetslipLinearTracking.LinearCheckbox : BetslipLinearTracking.LinearNoCheckbox;
    }
}
